/**
 * Everything the GM told the system, and the two things it told them back.
 *
 * A GM who set a campaign up weeks ago had no way to read their own answers:
 * the setup form is gone once a session starts, the opening narration scrolls
 * off the board within a few checkpoints, and the guide was a link that
 * replaced the whole dashboard — leaving a live session to read a paragraph.
 *
 * So this is a sheet over the board rather than a route. Mid-session the GM is
 * looking away from the table for a few seconds and wants the answer to be
 * already there; navigating away and back is the wrong shape for that, and on
 * a phone it would lose their place in the storyboard.
 *
 * The premise is displayed by section rather than as the raw blob. It is
 * compiled by campaign/premise.ts into a document addressed to the agent, and
 * about a third of it is planning instructions the GM never wrote. Showing
 * that verbatim would bury the four fields they actually filled in.
 */
import { useEffect, useRef, useState } from 'react';

import {
  getSessionSetup,
  type SessionSetup,
  userFacingError,
} from '@/api/client';
import { parseGuide, type Guide } from '@/guide/parse';

const GUIDE_URL = '/static/assets/docs/throughline-gm-guide.txt';

type Tab = 'campaign' | 'opening' | 'guide';

export function CampaignReference({
  sessionId,
  onClose,
}: {
  sessionId: string;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>('campaign');
  const [setup, setSetup] = useState<SessionSetup | null>(null);
  const [guide, setGuide] = useState<Guide | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<Element | null>(null);

  useEffect(() => {
    restoreTo.current = document.activeElement;
    dialog.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      (restoreTo.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    void getSessionSetup(sessionId)
      .then((s) => !cancelled && setSetup(s))
      .catch(
        (e) =>
          !cancelled &&
          setError(
            userFacingError(
              e,
              'The campaign reference couldn’t be loaded. Please try again.',
            ),
          ),
      );
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // The guide is 27KB of prose most sessions never open, so it is fetched only
  // when that tab is.
  useEffect(() => {
    if (tab !== 'guide' || guide) return;
    let cancelled = false;
    void fetch(GUIDE_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`guide unavailable (${r.status})`);
        return r.text();
      })
      .then((t) => !cancelled && setGuide(parseGuide(t)))
      .catch(
        () =>
          !cancelled &&
          setError('The GM Guide couldn’t be loaded. Please refresh and try again.'),
      );
    return () => {
      cancelled = true;
    };
  }, [tab, guide]);

  return (
    <div className="sheet" role="presentation" onClick={onClose}>
      <div
        className="sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Campaign reference"
        tabIndex={-1}
        ref={dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <nav className="sheet__tabs" aria-label="Reference sections">
            <TabButton on={tab === 'campaign'} onClick={() => setTab('campaign')} label="Campaign" />
            <TabButton on={tab === 'opening'} onClick={() => setTab('opening')} label="Opening" />
            <TabButton on={tab === 'guide'} onClick={() => setTab('guide')} label="Guide" />
          </nav>
          <button type="button" className="button button--quiet" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="sheet__body">
          {error ? <p className="history__error">{error}</p> : null}
          {tab === 'campaign' ? <CampaignTab setup={setup} /> : null}
          {tab === 'opening' ? <OpeningTab setup={setup} /> : null}
          {tab === 'guide' ? <GuideTab guide={guide} /> : null}
        </div>
      </div>
    </div>
  );
}

function TabButton({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className={on ? 'sheet__tab sheet__tab--on' : 'sheet__tab'}
      onClick={onClick}
      aria-current={on ? 'page' : undefined}
    >
      {label}
    </button>
  );
}

function CampaignTab({ setup }: { setup: SessionSetup | null }) {
  if (!setup) return <p className="history__empty">Loading…</p>;
  const sections = parsePremise(setup.premise);
  return (
    <>
      <h2 className="history__title">{setup.name}</h2>

      {setup.session_intent ? (
        <Block title="What you wanted out of tonight">{setup.session_intent}</Block>
      ) : null}

      {sections.map((s) => (
        <Block key={s.label} title={s.label}>
          {s.body}
        </Block>
      ))}

      <section className="history__section">
        <h3 className="history__heading">Party</h3>
        {setup.party.length === 0 ? (
          <p className="history__empty">
            No party on file, so fights are balanced for a stand-in of four
            level-3 characters. Add one in campaign setup.
          </p>
        ) : (
          <ul className="ref__party">
            {setup.party.map((p, i) => (
              <li key={`${p.name}-${i}`}>
                <strong>{p.name}</strong>
                {p.player_name ? <span className="ref__dim"> · {p.player_name}</span> : null}
                <span className="ref__dim">
                  {' '}
                  · {p.level ? `level ${p.level}` : 'no level set'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {setup.system_notes ? (
        <Block title="House rules">{setup.system_notes}</Block>
      ) : null}
      {setup.system_key ? (
        <p className="ref__dim">System: {setup.system_key}</p>
      ) : null}
    </>
  );
}

function OpeningTab({ setup }: { setup: SessionSetup | null }) {
  if (!setup) return <p className="history__empty">Loading…</p>;
  if (!setup.opening_narration) {
    return (
      <p className="history__empty">
        No opening narration yet. It is written on the first tick, a few seconds
        after the session starts.
      </p>
    );
  }
  return (
    <>
      <h2 className="history__title">Opening narration</h2>
      <p className="history__subtitle">
        What Throughline wrote to open the night. Yours to read aloud or
        rewrite.
      </p>
      <div className="narration">
        {setup.opening_narration.split(/\n{2,}/).map((para, i) => (
          <p key={i} className="ref__para">
            {para}
          </p>
        ))}
      </div>
    </>
  );
}

function GuideTab({ guide }: { guide: Guide | null }) {
  if (!guide) return <p className="history__empty">Loading the guide…</p>;
  return (
    <>
      <h2 className="history__title">{guide.title}</h2>
      {guide.subtitle ? <p className="history__subtitle">{guide.subtitle}</p> : null}
      {/* Collapsed by default. A GM opening this mid-session wants one answer,
          and 27KB of prose expanded is not a way to find it. */}
      {guide.sections.map((section) => (
        <details key={section.id} className="ref__section">
          <summary className="ref__summary">
            {section.number !== null ? `${section.number}. ` : ''}
            {section.title}
          </summary>
          {section.blocks.map((block, i) =>
            block.kind === 'list' ? (
              <ul key={i} className="ref__list">
                {block.lines.map((line, j) => (
                  <li key={j}>{line}</li>
                ))}
              </ul>
            ) : (
              <p key={i} className="ref__para">
                {block.lines.join(' ')}
              </p>
            ),
          )}
        </details>
      ))}
    </>
  );
}

function Block({ title, children }: { title: string; children: string }) {
  return (
    <section className="history__section">
      <h3 className="history__heading">{title}</h3>
      {children.split(/\n{2,}/).map((para, i) => (
        <p key={i} className="ref__para">
          {para}
        </p>
      ))}
    </section>
  );
}

/**
 * Pull the GM's own answers back out of the compiled premise.
 *
 * campaign/premise.ts writes `Label: value` lines under headed sections, and
 * appends a LIVE PLANNING INSTRUCTIONS block addressed to the agent. Only the
 * labelled fields are things the GM typed, so only those come back — the rest
 * is the system talking to itself and would read as noise here.
 *
 * Deliberately tolerant: an unparseable premise falls back to showing the whole
 * blob rather than showing nothing, because a wall of text a GM can search is
 * far better than an empty panel.
 */
export function parsePremise(premise: string): { label: string; body: string }[] {
  if (!premise?.trim()) return [];
  const wanted = [
    'World, setting, and premise',
    'Where the session begins',
    'Tone and pacing',
    'GM strengths, weaknesses, and support needs',
  ];
  const out: { label: string; body: string }[] = [];
  for (const label of wanted) {
    // compilePremise writes these as `- Label: value`, so the dash is part of
    // the shape. Stop at the next `- ` field or at a section header — a line
    // that is entirely uppercase — rather than at the first blank line, because
    // a world or tone field is a textarea and its paragraph breaks are real.
    const re = new RegExp(
      `^-\\s*${escapeRe(label)}:\\s*([\\s\\S]*?)(?=\\n- |\\n[A-Z][A-Z ]{2,}\\n|$)`,
      'm',
    );
    const found = premise.match(re);
    const body = found?.[1]?.trim();
    if (body && body !== '(not provided)') out.push({ label, body });
  }
  if (out.length === 0) {
    const whole = premise.split('LIVE PLANNING INSTRUCTIONS')[0] ?? premise;
    return [{ label: 'Setup', body: whole.trim() }];
  }
  return out;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
